import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api/errors';
import { CORE_API_UNAUTHORIZED_EVENT, normalizeApiError } from '../api/coreApi';
import { environment } from '../config/environment';
import { loginWithPassword, verifyAuthenticatedUser } from './authService';
import { canAccessCommandCentre as canAccessCommandCentreByRole } from './commandCentreAccess';
import { isAdministratorRole } from './role';
import { clearAccessToken, getAccessToken } from './tokenStorage';
import { AuthContextValue, AuthenticatedUser, AuthStatus } from './types';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthState {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  error: string | null;
}

const forbiddenMessage =
  'Your Elite Core account does not currently have Command Centre access.';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    status: 'initializing',
    user: null,
    error: null,
  });
  const lastActivityAtRef = useRef<number>(Date.now());

  const applyAuthenticatedUser = useCallback((user: AuthenticatedUser) => {
    if (!canAccessCommandCentreByRole(user.platformRole, user.commandCentreAccess?.enabled === true)) {
      setState({
        status: 'forbidden',
        user: null,
        error: forbiddenMessage,
      });
      return;
    }

    lastActivityAtRef.current = Date.now();
    setState({
      status: 'authenticated',
      user,
      error: null,
    });
  }, []);

  const refreshAuth = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setState({ status: 'unauthenticated', user: null, error: null });
      return;
    }

    setState((previous) => ({
      ...previous,
      status: 'initializing',
      error: null,
    }));

    try {
      const user = await verifyAuthenticatedUser();
      applyAuthenticatedUser(user);
    } catch (error) {
      const normalized = normalizeApiError(error);
      if (normalized.kind === 'authorization') {
        clearAccessToken();
        setState({ status: 'unauthenticated', user: null, error: null });
        return;
      }

      if (normalized.kind === 'forbidden') {
        setState({ status: 'forbidden', user: null, error: normalized.message || forbiddenMessage });
        return;
      }

      setState({ status: 'error', user: null, error: normalized.message });
    }
  }, [applyAuthenticatedUser]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setState({ status: 'unauthenticated', user: null, error: null });
    };

    window.addEventListener(CORE_API_UNAUTHORIZED_EVENT, handleUnauthorized);
    refreshAuth();

    return () => {
      window.removeEventListener(CORE_API_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [refreshAuth]);

  useEffect(() => {
    if (state.status !== 'authenticated') {
      return undefined;
    }

    const markActivity = () => {
      lastActivityAtRef.current = Date.now();
    };

    const listeners: Array<keyof WindowEventMap> = ['click', 'keydown', 'mousemove', 'focus'];
    listeners.forEach((eventName) => window.addEventListener(eventName, markActivity));

    const timer = window.setInterval(() => {
      const maxIdleMs = environment.sessionTimeoutMinutes * 60 * 1000;
      if (Date.now() - lastActivityAtRef.current >= maxIdleMs) {
        clearAccessToken();
        setState({
          status: 'unauthenticated',
          user: null,
          error: 'Your Command Centre session timed out. Please sign in again.',
        });
      }
    }, 60 * 1000);

    return () => {
      listeners.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      window.clearInterval(timer);
    };
  }, [state.status]);

  const login = useCallback(async (email: string, password: string) => {
    setState({ status: 'initializing', user: null, error: null });

    try {
      const user = await loginWithPassword(email, password);
      applyAuthenticatedUser(user);
    } catch (error) {
      const normalized = normalizeApiError(error) as ApiError;

      if (normalized.kind === 'authorization' || normalized.kind === 'validation') {
        clearAccessToken();
        setState({
          status: 'unauthenticated',
          user: null,
          error: normalized.message,
        });
        return;
      }

      if (normalized.kind === 'forbidden') {
        setState({
          status: 'forbidden',
          user: null,
          error: normalized.message || forbiddenMessage,
        });
        return;
      }

      clearAccessToken();
      setState({
        status: 'error',
        user: null,
        error: normalized.message,
      });
    }
  }, [applyAuthenticatedUser]);

  const logout = useCallback(() => {
    clearAccessToken();
    setState({ status: 'unauthenticated', user: null, error: null });
  }, []);

  const clearError = useCallback(() => {
    setState((previous) => ({ ...previous, error: null }));
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const hasCommandCentreAccess = canAccessCommandCentreByRole(state.user?.platformRole, state.user?.commandCentreAccess?.enabled === true);

    return {
      status: state.status,
      user: state.user,
      error: state.error,
      isAdministrator: isAdministratorRole(state.user?.role),
      canAccessCommandCentre: hasCommandCentreAccess,
      login,
      logout,
      refreshAuth,
      clearError,
    };
  }, [clearError, login, logout, refreshAuth, state.error, state.status, state.user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
